import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Loader2, Check, Music, FileAudio, MessageSquare, Search, Save, XCircle, AlertTriangle, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useProcessing } from '../contexts/ProcessingContext';

const PHASE_CONFIG = {
  uploading: {
    title: "Uploading",
    icon: FileAudio,
    defaultMessage: "Uploading your track...",
    estimatedTime: "~10 seconds"
  },
  processing: {
    title: "Starting",
    icon: Music,
    defaultMessage: "Getting everything ready...",
    estimatedTime: "~5 seconds"
  },
  separating: {
    title: "Separating",
    icon: Music,
    defaultMessage: "Separating vocals from instrumental...",
    estimatedTime: "~45 seconds"
  },
  transcribing: {
    title: "Transcribing",
    icon: MessageSquare,
    defaultMessage: "Transcribing lyrics with AI...",
    estimatedTime: "~30 seconds"
  },
  detecting: {
    title: "Syncing",
    icon: Search,
    defaultMessage: "Syncing lyrics with audio...",
    estimatedTime: "~5 seconds"
  },
  finalizing: {
    title: "Finalizing",
    icon: Save,
    defaultMessage: "Saving results...",
    estimatedTime: "~5 seconds"
  },
  complete: {
    title: "Complete",
    icon: Check,
    defaultMessage: "Your karaoke track is ready!",
    estimatedTime: ""
  },
  error: {
    title: "Error",
    icon: FileAudio,
    defaultMessage: "Something went wrong",
    estimatedTime: ""
  },
  cancelled: {
    title: "Cancelled",
    icon: XCircle,
    defaultMessage: "Processing was cancelled",
    estimatedTime: ""
  }
};

const PHASES_ORDER = ['uploading', 'separating', 'transcribing', 'detecting', 'finalizing', 'complete'];

function PhaseIndicator({ phase, currentPhase, isComplete }) {
  const config = PHASE_CONFIG[phase] || PHASE_CONFIG.processing;
  const Icon = config.icon;
  
  const phaseIndex = PHASES_ORDER.indexOf(phase);
  // Map 'processing' to 'uploading' since it's just a transitional state
  const mappedCurrentPhase = currentPhase === 'processing' ? 'uploading' : currentPhase;
  const currentIndex = PHASES_ORDER.indexOf(mappedCurrentPhase);
  const isPast = phaseIndex < currentIndex || isComplete;
  const isCurrent = phase === mappedCurrentPhase && !isComplete;
  
  return (
    <div className={`flex items-center gap-2 py-1.5 transition-all duration-300 ${
      isPast ? 'text-green-500' : 
      isCurrent ? 'text-primary font-medium' : 
      'text-muted-foreground/50'
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
        isPast ? 'bg-green-500/20' : 
        isCurrent ? 'bg-primary/20' : 
        'bg-muted/30'
      }`}>
        {isPast ? (
          <Check className="w-3.5 h-3.5" />
        ) : isCurrent ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </div>
      <span className="text-sm">{config.title}</span>
    </div>
  );
}

export default function ProcessingModal({ isOpen, status, sessionId, onClose, onCancel, onBackground }) {
  const { moveToBackground } = useProcessing();
  const [progress, setProgress] = useState({
    phase: status || 'uploading',
    message: 'Getting everything ready...',
    percent: 5,
    sub_progress: 0
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const pollIntervalRef = useRef(null);

  const handleFinishInBackground = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    moveToBackground(sessionId);
    if (onBackground) onBackground();
    if (onClose) onClose();
  };

  const handleCancel = async () => {
    if (!sessionId) return;
    
    setIsCancelling(true);
    try {
      const response = await api.post(`/sessions/${sessionId}/cancel`);
      const data = response.data;
      
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      
      setProgress({
        phase: 'cancelled',
        message: 'Processing cancelled by user',
        percent: 0,
        sub_progress: 0
      });
      
      if (data.minutes_refunded > 0) {
        toast.success(`Processing cancelled. ${data.minutes_refunded} minute${data.minutes_refunded !== 1 ? 's' : ''} refunded.`);
      } else {
        toast.success('Processing cancelled.');
      }
      
      if (onCancel) {
        onCancel(data);
      }
      
      setShowCancelConfirm(false);
      setTimeout(() => onClose && onClose(), 1500);
    } catch (error) {
      console.error('Error cancelling:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to cancel processing';
      toast.error(errorMsg);
      setShowCancelConfirm(false);
    } finally {
      setIsCancelling(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !sessionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/status`);
        const data = response.data;
        
        if (data.progress) {
          setProgress(data.progress);
        } else {
          setProgress(prev => ({
            ...prev,
            phase: data.status,
            percent: data.status === 'complete' ? 100 : prev.percent
          }));
        }
        
        if (data.status === 'error') {
          setErrorMessage(data.error_message || 'An error occurred during processing');
        }
        
        // Stop polling if complete, error, or cancelled
        if (data.status === 'complete' || data.status === 'error' || data.status === 'cancelled') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    // Initial poll
    pollStatus();
    
    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(pollStatus, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, sessionId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProgress({
        phase: status || 'uploading',
        message: 'Getting everything ready...',
        percent: 5,
        sub_progress: 0
      });
      setErrorMessage(null);
      setShowCancelConfirm(false);
      setIsCancelling(false);
    }
  }, [isOpen, status]);

  const currentPhase = progress.phase || status || 'uploading';
  const phaseConfig = PHASE_CONFIG[currentPhase] || PHASE_CONFIG.processing;
  const isComplete = currentPhase === 'complete';
  const isError = currentPhase === 'error';
  const isCancelled = currentPhase === 'cancelled';
  const isProcessing = !isComplete && !isError && !isCancelled;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="processing-modal" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {isProcessing && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isComplete && <Check className="h-5 w-5 text-green-500" />}
            {isError && <AlertTriangle className="h-5 w-5 text-red-500" />}
            {isCancelled && <XCircle className="h-5 w-5 text-amber-500" />}
            <span>
              {isComplete ? 'Processing Complete!' : 
               isError ? 'Processing Failed' : 
               isCancelled ? 'Processing Cancelled' :
               'Processing Your Track'}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progress.message || phaseConfig.defaultMessage}</span>
              <span className="text-primary font-medium">{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="w-full h-2" data-testid="processing-progress" />
          </div>
          
          {/* Phase timeline */}
          <div className="border rounded-lg p-3 bg-muted/20">
            <div className="space-y-0.5">
              {PHASES_ORDER.map((phase) => (
                <PhaseIndicator 
                  key={phase} 
                  phase={phase} 
                  currentPhase={currentPhase}
                  isComplete={isComplete}
                />
              ))}
            </div>
          </div>
          
          {/* Error message */}
          {isError && errorMessage && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{errorMessage}</p>
            </div>
          )}
          
          {/* Estimated time hint */}
          {isProcessing && phaseConfig.estimatedTime && (
            <p className="text-xs text-center text-muted-foreground">
              This step typically takes {phaseConfig.estimatedTime}
            </p>
          )}
          
          {isComplete && (
            <p className="text-sm text-center text-green-500">
              Your karaoke track is ready - time to sing!
            </p>
          )}
          
          {isCancelled && (
            <p className="text-sm text-center text-amber-500">
              Processing was cancelled. Your minutes have been refunded.
            </p>
          )}
          
          {/* Close button for cancelled/error states */}
          {(isCancelled || isError) && (
            <div className="pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          )}
          
          {/* Action buttons */}
          {isProcessing && !showCancelConfirm && (
            <div className="pt-2 border-t space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleFinishInBackground}
              >
                <Minimize2 className="w-4 h-4 mr-2" />
                Finish in Background
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground hover:text-destructive"
                onClick={() => setShowCancelConfirm(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Processing
              </Button>
            </div>
          )}
          
          {showCancelConfirm && (
            <div className="pt-2 border-t space-y-2">
              <p className="text-sm text-center text-muted-foreground">
                Are you sure? Your minutes will be refunded.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                >
                  Keep Processing
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  onClick={handleCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
